/**
 * Hard ceiling on rendered tiles, regardless of panel size. Below this, every
 * deduped respondent renders (panel-size-driven). Above it, tiles are sampled
 * down deterministically — see {@link buildPortraitMosaicTiles}.
 */
export const PORTRAIT_MOSAIC_TILE_CAP = 240

/**
 * Column count when the strip has not been measured yet (jsdom, first paint).
 * 8 divides the tile cap evenly, so unmeasured renders keep a full last row.
 */
export const MOSAIC_COLUMN_COUNT_FALLBACK = 8

/**
 * Target tile size in px (Step 5b visual QA finding 4): columns are derived
 * from the strip's OWN measured width so tiles land in the ~44-56px range
 * regardless of how wide the band's host container is. The old fixed
 * breakpoint table (8/12/16/24 at viewport-ish widths) ignored the actual
 * container width — a 720px band still landed on the 24-col step and drew
 * 28px micro-tiles. `Math.round(width / MOSAIC_TARGET_TILE_PX)` centers on
 * this target; {@link MOSAIC_MIN_COLUMNS}/{@link MOSAIC_MAX_COLUMNS} bound it
 * so a very narrow or very wide band never goes below a legible density or
 * above the study-doc article cap's (42rem) old ceiling.
 */
export const MOSAIC_TARGET_TILE_PX = 48
export const MOSAIC_MIN_COLUMNS = 8
export const MOSAIC_MAX_COLUMNS = 24

/**
 * Persona portraits are 3:4. Every mosaic tile — photo or initials fallback —
 * uses this box so a mixed row stays one height and the grid does not break
 * around an unconstrained `<img>` intrinsic size.
 */
export const PORTRAIT_MOSAIC_TILE_ASPECT_CLASS = 'aspect-[3/4]'

/**
 * Stretch UserAvatar past its fixed `lg` (64px) square so the initials slab
 * fills the same 3:4 cell as a real portrait. `border-0` drops the themeTinted
 * hairline that would otherwise inset the fallback against edge-to-edge photos.
 */
export const PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS = 'h-full w-full rounded-none border-0'

/**
 * Column count for a measured strip width. Non-positive / non-finite widths
 * (jsdom `getBoundingClientRect` is 0) fall back to
 * {@link MOSAIC_COLUMN_COUNT_FALLBACK} so tests and first paint still lay out
 * a full-row grid.
 */
export function mosaicColumnCount(containerWidthPx: number): number {
  if (!Number.isFinite(containerWidthPx) || containerWidthPx <= 0) {
    return MOSAIC_COLUMN_COUNT_FALLBACK
  }

  const target = Math.round(containerWidthPx / MOSAIC_TARGET_TILE_PX)
  return Math.min(MOSAIC_MAX_COLUMNS, Math.max(MOSAIC_MIN_COLUMNS, target))
}

/**
 * Pre-generated portrait thumbnail sizes (see `personaThumbUrl`).
 */
export type MosaicThumbSize = 64 | 128 | 256

/**
 * Thumbnail variant for a tile's RENDERED size, in physical pixels: the CSS
 * tile size times the device pixel ratio. The band's tiles land around
 * {@link MOSAIC_TARGET_TILE_PX} CSS px but stretch toward ~80px in a
 * full-viewport band, and retina displays double that — requesting t64 there
 * paints visibly pixelated faces (live QA 2026-09-03). Unmeasured (jsdom,
 * first paint before layout) defaults to 128: sharp on retina at the target
 * size without jumping straight to the largest variant.
 */
export function mosaicThumbSize(tileCssPx: number, devicePixelRatio: number): MosaicThumbSize {
  const ratio =
    Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1
  const needed = tileCssPx * ratio
  if (!Number.isFinite(needed) || needed <= 0) {
    return 128
  }
  if (needed <= 64) {
    return 64
  }
  if (needed <= 128) {
    return 128
  }
  return 256
}

/**
 * Round a tile count down to complete rows under the cap. An incomplete first
 * row is kept so a small panel is not dropped. `columns` < 1 is a no-op.
 */
export function mosaicTileCountForFullRows(
  tileCount: number,
  columns: number,
  cap: number = PORTRAIT_MOSAIC_TILE_CAP,
): number {
  const bounded = Math.min(Math.max(tileCount, 0), cap)
  if (columns < 1) {
    return bounded
  }
  const rowCap = Math.floor(cap / columns) * columns
  const limited = Math.min(bounded, rowCap)
  if (limited < columns) {
    return limited
  }
  return Math.floor(limited / columns) * columns
}

export interface PortraitMosaicResponse {
  persona_id?: string
  attributes?: Record<string, unknown> | null
}

export type PortraitMosaicImageSource = 'response' | 'picker' | 'none'

export interface PortraitMosaicTile {
  personaId: string
  name: string
  initials: string
  imageUrl?: string
  imageSource: PortraitMosaicImageSource
  isHighlighted: boolean
}

export interface BuildPortraitMosaicTilesOptions {
  highlightedPersonaIds?: Array<string>
  pickerImages?: Record<string, string>
  /**
   * When set, the returned tile list is rounded down to complete rows at this
   * column count (under {@link PORTRAIT_MOSAIC_TILE_CAP}). Omitted → today's
   * cap-only sampling, no orphan trim.
   */
  columns?: number
}

function stringAttr(attrs: Record<string, unknown>, keys: Array<string>): string {
  for (const key of keys) {
    const raw = attrs[key]
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim()
    }
  }
  return ''
}

export function mosaicInitialsFromName(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )
}

function nameFromAttributes(attrs: Record<string, unknown>): string {
  return stringAttr(attrs, ['display_name', 'name', 'persona_name']) || 'Respondent'
}

function responseImageFromAttributes(attrs: Record<string, unknown>): string {
  return stringAttr(attrs, ['image_url', 'imageUrl'])
}

/**
 * Deterministic 32-bit hash (FNV-1a) of a persona id. Used only to seed the
 * proportional sampling below — never `Math.random()` — so the same input
 * panel always samples down to the same rendered set.
 */
function hashPersonaId(personaId: string): number {
  let hash = 0x811c9dc5
  for (let index = 0; index < personaId.length; index += 1) {
    hash ^= personaId.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

function pickSeeded(pool: Array<PortraitMosaicTile>, count: number): Array<string> {
  return [...pool]
    .sort((a, b) => hashPersonaId(a.personaId) - hashPersonaId(b.personaId))
    .slice(0, Math.max(0, count))
    .map((tile) => tile.personaId)
}

/**
 * When the deduped panel exceeds `cap`, sample rendered tiles so the
 * lit/unlit split among the tiles that DO render tracks the true panel-wide
 * proportion (`pct`) — so a "lit faces are the 82%" caption stays literally
 * true even though only `cap` tiles are on screen. Sampling is deterministic
 * (seeded on persona id via {@link hashPersonaId}): the same panel + the same
 * highlight set always produces the same sampled tiles. Rendering order
 * follows the original (first-occurrence) order, not the sampling order.
 */
function sampleTilesToCap(
  tiles: Array<PortraitMosaicTile>,
  cap: number,
): Array<PortraitMosaicTile> {
  if (tiles.length <= cap) {
    return tiles
  }

  const highlighted = tiles.filter((tile) => tile.isHighlighted)
  const dim = tiles.filter((tile) => !tile.isHighlighted)
  const pct = highlighted.length / tiles.length

  let litTarget = Math.min(Math.round(pct * cap), highlighted.length)
  let dimTarget = cap - litTarget
  if (dimTarget > dim.length) {
    const shortfall = dimTarget - dim.length
    dimTarget = dim.length
    litTarget = Math.min(litTarget + shortfall, highlighted.length)
  }

  const pickedIds = new Set([...pickSeeded(highlighted, litTarget), ...pickSeeded(dim, dimTarget)])

  return tiles.filter((tile) => pickedIds.has(tile.personaId))
}

/**
 * Build mosaic tiles from `result.responses[]` only — `persona_id` plus
 * `attributes.image_url` / `attributes.imageUrl`. No persona-record fetch.
 *
 * First occurrence of each persona_id wins (monadic rows repeat). Every
 * deduped respondent renders up to the {@link PORTRAIT_MOSAIC_TILE_CAP} hard
 * ceiling (panel-size-driven, not a fixed small cap); past the ceiling, tiles
 * are sampled proportionally per {@link sampleTilesToCap}.
 * `highlightedPersonaIds` omitted → every tile is full color. When the array
 * is provided (including empty), only listed ids are highlighted.
 * When `columns` is set, the cap is rounded down to complete rows so the last
 * row has no orphans (an incomplete first row is kept).
 */
export function buildPortraitMosaicTiles(
  responses: Array<PortraitMosaicResponse>,
  options: BuildPortraitMosaicTilesOptions = {},
): Array<PortraitMosaicTile> {
  const highlightSet =
    options.highlightedPersonaIds === undefined
      ? null
      : new Set(options.highlightedPersonaIds.filter(Boolean))
  const pickerImages = options.pickerImages ?? {}

  const seen = new Set<string>()
  const tiles: Array<PortraitMosaicTile> = []

  for (const row of responses) {
    const personaId = typeof row.persona_id === 'string' ? row.persona_id.trim() : ''
    if (!personaId || seen.has(personaId)) {
      continue
    }
    seen.add(personaId)

    const attrs = (row.attributes ?? {}) as Record<string, unknown>
    const responseImage = responseImageFromAttributes(attrs)
    const pickerImage = (pickerImages[personaId] ?? '').trim()
    const imageUrl = responseImage || pickerImage || undefined
    const name = nameFromAttributes(attrs)

    tiles.push({
      personaId,
      name,
      initials: mosaicInitialsFromName(name),
      imageUrl,
      imageSource: responseImage ? 'response' : pickerImage ? 'picker' : 'none',
      isHighlighted: highlightSet === null || highlightSet.has(personaId),
    })
  }

  return sampleTilesToCap(
    tiles,
    mosaicTileCountForFullRows(tiles.length, options.columns ?? 0),
  )
}
